import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import theme from '../../theme';
import * as api from './groceryApi';

var CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'produce', label: '🥬 Produce', color: '#4ade80' },
  { id: 'protein', label: '🥩 Protein', color: '#f87171' },
  { id: 'dairy', label: '🧀 Dairy', color: '#fbbf24' },
  { id: 'grain', label: '🌾 Grain', color: '#d97706' },
  { id: 'pantry', label: '🫙 Pantry', color: '#a78bfa' },
  { id: 'frozen', label: '🧊 Frozen', color: '#22d3ee' },
  { id: 'other', label: '📦 Other', color: '#94a3b8' },
];

var Page = styled.div`
  padding: 16px;
  max-width: 700px;
  margin: 0 auto;
  color: ${theme.text};
`;

var AddRow = styled.form`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

var Input = styled.input`
  flex: 1;
  padding: 12px;
  border: 2px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: ${theme.bg};
  color: #fff;
  font-size: 1rem;
  min-height: 48px;
  &:focus { outline: none; border-color: ${theme.accent}; }
  &::placeholder { color: ${theme.textPlaceholder}; }
`;

var SmallInput = styled.input`
  width: 70px;
  padding: 12px;
  border: 2px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: ${theme.bg};
  color: #fff;
  font-size: 0.9rem;
  min-height: 48px;
  &:focus { outline: none; border-color: ${theme.accent}; }
  &::placeholder { color: ${theme.textPlaceholder}; }
`;

var Select = styled.select`
  padding: 12px;
  border: 2px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: ${theme.bg};
  color: #fff;
  font-size: 0.85rem;
  min-height: 48px;
  &:focus { outline: none; border-color: ${theme.accent}; }
`;

var AddBtn = styled.button`
  padding: 12px 20px;
  border: none;
  border-radius: ${theme.radiusSm};
  background: ${theme.accentGradient};
  color: #000;
  font-weight: 700;
  cursor: pointer;
  min-height: 48px;
  white-space: nowrap;
  &:hover { transform: translateY(-1px); }
`;

var Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

var Tab = styled.button`
  padding: 8px 14px;
  border: 2px solid ${p => p.$active ? (p.$color || theme.accent) : theme.border};
  border-radius: ${theme.radiusPill};
  background: ${p => p.$active ? 'rgba(74,222,128,0.1)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : theme.textMuted};
  cursor: pointer;
  font-size: 0.8rem;
  min-height: 40px;
  white-space: nowrap;
  &:hover { border-color: ${p => p.$color || theme.accent}; }
`;

var ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  margin-bottom: 6px;
  background: ${p => p.$checked ? 'rgba(255,255,255,0.02)' : theme.bgCard};
  opacity: ${p => p.$checked ? 0.5 : 1};
  transition: opacity 0.2s;
`;

var Checkbox = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid ${p => p.$checked ? theme.accent : theme.border};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${p => p.$checked ? theme.accent + '22' : 'transparent'};
  color: ${theme.accent};
  font-size: 14px;
  &:hover { border-color: ${theme.accent}; }
`;

var ItemName = styled.span`
  flex: 1;
  color: ${p => p.$checked ? theme.textDim : '#fff'};
  text-decoration: ${p => p.$checked ? 'line-through' : 'none'};
  font-size: 0.95rem;
`;

var ItemMeta = styled.span`
  color: ${theme.textMuted};
  font-size: 0.8rem;
  white-space: nowrap;
`;

var CatDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$color || theme.textMuted};
  flex-shrink: 0;
`;

var DeleteBtn = styled.button`
  background: none;
  border: none;
  color: ${theme.textDim};
  cursor: pointer;
  font-size: 1rem;
  padding: 4px;
  &:hover { color: ${theme.error}; }
`;

var ActionBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

var ActionBtn = styled.button`
  padding: 8px 16px;
  border: 1px solid ${theme.border};
  border-radius: ${theme.radiusSm};
  background: transparent;
  color: ${p => p.$color || theme.textMuted};
  cursor: pointer;
  font-size: 0.8rem;
  &:hover { border-color: ${p => p.$color || theme.accent}; }
`;

var Empty = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${theme.textDim};
  font-size: 1.1rem;
`;

var Count = styled.span`
  color: ${theme.textMuted};
  font-size: 0.85rem;
`;

var getCatColor = function (cat) {
  var found = CATEGORIES.find(function (c) { return c.id === cat; });
  return found ? found.color : theme.textMuted;
};

var GroceryListPage = function ({ onLogout }) {
  var [list, setList] = useState(null);
  var [loading, setLoading] = useState(true);
  var [name, setName] = useState('');
  var [amount, setAmount] = useState('');
  var [unit, setUnit] = useState('');
  var [category, setCategory] = useState('other');
  var [filterCat, setFilterCat] = useState('all');

  var load = useCallback(async function () {
    try {
      setLoading(true);
      var data = await api.fetchList();
      setList(data);
    } catch (err) {
      if (err.message.includes('Session expired')) { onLogout(); return; }
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(function () { load(); }, [load]);

  var handleAdd = async function (e) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      var data = await api.addItem({ name: name.trim(), amount, unit, category });
      setList(data);
      setName('');
      setAmount('');
      setUnit('');
    } catch (err) {}
  };

  var handleToggle = async function (itemId) {
    try {
      var data = await api.toggleItem(itemId);
      setList(data);
    } catch (err) {}
  };

  var handleRemove = async function (itemId) {
    try {
      var data = await api.removeItem(itemId);
      setList(data);
    } catch (err) {}
  };

  var handleClearChecked = async function () {
    try {
      var data = await api.clearChecked();
      setList(data);
    } catch (err) {}
  };

  var handleClearAll = async function () {
    if (!window.confirm('Clear entire list?')) return;
    try {
      var data = await api.clearAll();
      setList(data);
    } catch (err) {}
  };

  var items = list?.items || [];
  var filtered = filterCat === 'all' ? items : items.filter(function (i) { return i.category === filterCat; });
  var unchecked = filtered.filter(function (i) { return !i.checked; });
  var checked = filtered.filter(function (i) { return i.checked; });
  var checkedCount = items.filter(function (i) { return i.checked; }).length;

  if (loading) return <Page><Empty>Loading...</Empty></Page>;

  return (
    <Page>
      <AddRow onSubmit={handleAdd}>
        <Input value={name} onChange={function (e) { setName(e.target.value); }} placeholder="Add item..." />
        <SmallInput value={amount} onChange={function (e) { setAmount(e.target.value); }} placeholder="Qty" />
        <SmallInput value={unit} onChange={function (e) { setUnit(e.target.value); }} placeholder="Unit" />
        <Select value={category} onChange={function (e) { setCategory(e.target.value); }}>
          {CATEGORIES.filter(function (c) { return c.id !== 'all'; }).map(function (c) {
            return <option key={c.id} value={c.id}>{c.label}</option>;
          })}
        </Select>
        <AddBtn type="submit">+</AddBtn>
      </AddRow>

      <Tabs>
        {CATEGORIES.map(function (c) {
          var count = c.id === 'all' ? items.length : items.filter(function (i) { return i.category === c.id; }).length;
          if (c.id !== 'all' && count === 0) return null;
          return (
            <Tab key={c.id} $active={filterCat === c.id} $color={c.color}
              onClick={function () { setFilterCat(c.id); }}>
              {c.label} ({count})
            </Tab>
          );
        })}
      </Tabs>

      {checkedCount > 0 && (
        <ActionBar>
          <ActionBtn $color={theme.warning} onClick={handleClearChecked}>Clear {checkedCount} checked</ActionBtn>
          <ActionBtn $color={theme.error} onClick={handleClearAll}>Clear all</ActionBtn>
        </ActionBar>
      )}

      {filtered.length === 0 ? (
        <Empty>No items yet. Add something above.</Empty>
      ) : (
        <div>
          {unchecked.map(function (item) {
            return (
              <ItemRow key={item._id} $checked={false}>
                <Checkbox $checked={false} onClick={function () { handleToggle(item._id); }} />
                <CatDot $color={getCatColor(item.category)} />
                <ItemName $checked={false}>{item.name}</ItemName>
                {(item.amount || item.unit) && <ItemMeta>{item.amount} {item.unit}</ItemMeta>}
                <DeleteBtn onClick={function () { handleRemove(item._id); }}>✕</DeleteBtn>
              </ItemRow>
            );
          })}
          {checked.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Count>{checked.length} checked</Count>
              {checked.map(function (item) {
                return (
                  <ItemRow key={item._id} $checked={true}>
                    <Checkbox $checked={true} onClick={function () { handleToggle(item._id); }}>✓</Checkbox>
                    <CatDot $color={getCatColor(item.category)} />
                    <ItemName $checked={true}>{item.name}</ItemName>
                    {(item.amount || item.unit) && <ItemMeta>{item.amount} {item.unit}</ItemMeta>}
                    <DeleteBtn onClick={function () { handleRemove(item._id); }}>✕</DeleteBtn>
                  </ItemRow>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Page>
  );
};

export default GroceryListPage;
